import {
  dispatch,
  getChannelMessages,
  getAllIssueThreads,
  store,
  updateChatActionDrawer,
  updateSelectedMessage,
  getChannelById,
  RedingtonMediaRequest,
} from '@zonofi/seller-rtk-store';
import {
  ActionData,
  ChatActionType,
  GenerateLinkParams,
  MessageActionType,
  MessageActionValue,
  MessageEnvelope,
  MessageTagData,
  MessageType,
  OrderMessage,
  PaymentMessage,
  TemplateKeys,
  displayDateTime,
  getConfigValue,
  hash,
  splitPhoneNumber,
  theme,
  titleTruncate,
  MsgReactions,
} from '@zonofi/common';
import { camelCase, isEmpty, isUndefined, startCase, uniqBy } from 'lodash';
import { useGetTemplateFromData } from './get-template';
import {
  generateMediaParams,
  getCampaignStatus,
  getMessageActionsData,
  getValidUnifiedId,
  isOwnMessage,
  parsePhoneFromVCard,
} from '../functions';
import { useGetMessageActionsMap } from './get-message-actions';
import { MessageActionKeys, MessageVariant, ReactionList } from '../components';
import { useGetCurrentChannelData } from './get-current-channel-data';
import { useMemo } from 'react';
import { ThreadInfo } from '../types';

interface TextActionButtons {
  text: string;
  onPress: () => void;
  color: 'default' | 'green';
}

const getMessageVariant = (
  uuid: string,
  channelId?: string
): MessageVariant => {
  let isOwn = false;
  if (channelId) {
    const channel = getChannelById(channelId)(store.getState());
    isOwn = isOwnMessage(uuid, channel);
  } else {
    isOwn = isOwnMessage(uuid);
  }
  return isOwn ? 'sender' : 'receiver';
};

const getUserNameColor = (title: string) => {
  const colors = theme.nameColors;
  const color = colors[hash(title, colors.length)];
  return color;
};

const getThreadInfo = (threadsActions: MessageActionValue): ThreadInfo => {
  const allIssues = getAllIssueThreads(store.getState());
  const threadDetails = Object.entries(threadsActions || {})?.[0];
  const threadId = threadDetails?.[0];
  if (threadId) {
    const timetoken = threadDetails?.[1]?.[0]?.actionTimetoken as string;
    const current = allIssues?.[threadId];
    const latestMessageTimeToken = current?.latestMessageTimeToken || timetoken;
    return { threadId, timetoken, latestMessageTimeToken };
  }
  return undefined;
};

const processReactions = (reaction: MessageActionValue): ReactionList[] => {
  if (isEmpty(reaction)) {
    return [];
  }

  // Create maps for tracking user reactions and counts
  const reactionMap = new Map<string, number>();
  const userReactionMap = new Map<string, string>();

  // Process all reactions to get latest reaction per user
  Object.entries(reaction).forEach(([emoji, reactions]) => {
    const unifiedEmoji = getValidUnifiedId(emoji);

    // Get all reactions for this emoji, sorted by timestamp
    const sortedReactions = reactions
      .slice()
      .sort(
        (a: ActionData, b: ActionData) =>
          Number(b.actionTimetoken) - Number(a.actionTimetoken)
      );

    // Store only the latest reaction per user
    sortedReactions.forEach(({ uuid, actionTimetoken }) => {
      const existingReaction = userReactionMap.get(uuid);

      // Only update if this is a new reaction or a more recent one
      if (
        (!existingReaction ||
          Number(actionTimetoken) > Number(existingReaction.split('|')[1])) &&
        unifiedEmoji?.length !== 0
      ) {
        userReactionMap.set(uuid, `${unifiedEmoji}|${actionTimetoken}`);
      }
    });
  });

  // Count reactions from the user reaction map
  userReactionMap.forEach((value) => {
    const emoji = value.split('|')[0];
    reactionMap.set(emoji, (reactionMap.get(emoji) || 0) + 1);
  });

  // Convert map to array format and return
  return Array.from(reactionMap, ([emoji, count]) => ({ emoji, count }));
};

const getReactionList = (
  reaction?: MsgReactions,
  actionReactions?: MessageActionValue
): ReactionList[] => {
  if (!isEmpty(actionReactions)) {
    return processReactions(actionReactions);
  }

  if (reaction?.emoji) {
    const emoji = getValidUnifiedId(reaction.emoji);
    return emoji ? [{ emoji, count: 1 }] : [];
  }

  return [];
};

const MSG_ACTIONS_QUICK: MessageActionKeys[] = ['reply', 'forward'];

const getMessageTags = (
  messageTags?: MessageTagData[],
  actionTags?: MessageActionValue
): MessageTagData | null => {
  const latestTagKey = Object.entries(actionTags || {})
    .map(([key, tagActions]) => ({
      key,
      timetoken: Math.max(
        0,
        ...tagActions.map((action) => Number(action.actionTimetoken))
      ),
    }))
    .sort((a, b) => Number(b.timetoken) - Number(a.timetoken))[0]?.key;

  if (latestTagKey) {
    try {
      return JSON.parse(latestTagKey) as MessageTagData;
    } catch (error) {
      console.warn('Failed to parse message action tag:', latestTagKey, error);
    }
  }

  if (Array.isArray(messageTags) && messageTags.length) {
    return messageTags[0];
  }

  return null;
};

export const useGetMessageData = (envelope: MessageEnvelope) => {
  const {
    channel,
    message,
    uuid = '',
    publisher = '',
    actions,
    timetoken = 0,
    isInternalNote = false,
    displayTimetoken,
  } = envelope || {};
  const {
    _id: msgId,
    rFor,
    user,
    source,
    type: msgType,
    text,
    userDetails,
    data,
    sId,
    intent,
    waConfig,
    media = [],
    actionBy,
    reply,
    agent_metadata,
    reaction,
    tags,
    stats,
  } = message || {};

  const type =
    (msgType === MessageType.TYPEBOT ? intent : msgType) || MessageType.TEXT;

  const { customerId, isGroupThread, channelId } = useGetCurrentChannelData();
  const channelMessagesSelector = getChannelMessages(channel ?? channelId);
  const messages = channelMessagesSelector(store.getState());
  const repliedForMessage = messages?.find(
    (x) => !isUndefined(rFor) && !isEmpty(rFor) && x.message._id === rFor
  ) as MessageEnvelope;
  const repliedForMessageData = message?.replyData;
  const { msgActions, actionsLoading } = useGetMessageActionsMap(envelope);

  const actionsSection1 = useMemo(
    () =>
      Object.entries(msgActions)
        .filter(
          ([key, value]) =>
            MSG_ACTIONS_QUICK.includes(key as MessageActionKeys) &&
            value.condition
        )
        .map(([_, v]) => v),
    [msgActions]
  );
  const actionsSection2 = useMemo(
    () =>
      Object.entries(msgActions)
        .filter(
          ([key, value]) =>
            !MSG_ACTIONS_QUICK.includes(key as MessageActionKeys) &&
            value.condition
        )
        .map(([_, v]) => v),
    [msgActions]
  );

  const messageTag = getMessageTags(tags, actions?.tags);

  const tagValue = useMemo(
    () =>
      messageTag?.tag && messageTag?.tag !== 'personal'
        ? messageTag?.tag
        : undefined,
    [messageTag?.tag]
  );

  const subTagValue = useMemo(
    () =>
      messageTag?.sub_tag &&
      messageTag?.sub_tag !== 'unknown' &&
      messageTag?.sub_tag !== 'null'
        ? messageTag?.sub_tag
        : undefined,
    [messageTag?.sub_tag]
  );

  const {
    id: userId = '',
    fName,
    firstName,
    mobile,
  } = actionBy || user || userDetails || data?.user || data?.userDetails || {};
  const userNameColor = getUserNameColor(userId);
  const displayName = fName || firstName;
  const { dialCode, phone } = splitPhoneNumber(mobile || '');
  const formattedPhone = phone
    ? `${dialCode ? `${dialCode} ` : ''}${phone}`
    : '';
  const userName =
    isGroupThread && displayName && formattedPhone
      ? `${displayName} • ${formattedPhone}`
      : displayName || formattedPhone || '-';
  const isBot = userName === 'bot';
  const isWhatsapp = source === 'whatsapp';
  const isNotSystemMessage = source !== 'system';

  const { template, buttons, systemButtons, cards } =
    useGetTemplateFromData(envelope);
  // console.log(timetoken, template, buttons, systemButtons);
  const deleted = !!Object.keys(actions?.['deleted'] || {}).length;
  const reactionList = getReactionList(reaction, actions?.['reaction']);
  const groupedMessageStats = message?.groupedMessageStats;

  const statusActions = actions?.['status'] || {};
  const delivered =
    statusActions?.['delivered']?.map((val) => val.uuid).length || 0;
  const seen = statusActions?.['seen']?.map((val) => val.uuid).length || 0;

  const userStatusMap = getMessageActionsData(actions, 'status');
  const orderStatusMap = getMessageActionsData(actions, 'orderStatus');
  const statusMap = { ...orderStatusMap, ...userStatusMap };

  let failedToSend = null;

  if (stats && stats?.error) {
    failedToSend = stats?.error?.description;
  }

  if (
    !failedToSend &&
    groupedMessageStats?.length &&
    groupedMessageStats.every((groupedMessage) => groupedMessage?.stats?.error)
  ) {
    failedToSend =
      groupedMessageStats.find(
        (groupedMessage) => groupedMessage?.stats?.error?.description
      )?.stats?.error?.description || true;
  }
  const acceptCustomer = !isUndefined(actions?.['acceptCustomer']);

  const threadsActions = actions?.['thread'] || {};
  const connectedThread = getThreadInfo(threadsActions);

  const msgTime = displayDateTime(String(displayTimetoken), 'time');
  const shortText = titleTruncate(startCase(text || type), 50);

  const isForwarded = type === MessageType.FORWARD;
  const isIssue = type === MessageType.ISSUE;
  const variant = isInternalNote
    ? 'sender'
    : isIssue
      ? 'receiver'
      : getMessageVariant(sId || uuid || publisher, channel);

  let label = '';

  if (
    type === MessageType.ORDER &&
    (data as OrderMessage)?.userType === 'Sales'
  ) {
    label = `Order Created By ${userName} - Sales Rep`;
  } else if (
    type === MessageType.PAYMENT &&
    (data as PaymentMessage)?.userType === 'Sales'
  ) {
    label = `Payment Collected By ${userName} - Sales Rep`;
  }

  const getWarningVarName = (val: string) => {
    const varName = val?.toLowerCase()?.includes('ledger')
      ? 'Legder balance'
      : val?.toLowerCase()?.includes('invoice')
        ? 'Invoice records'
        : 'Outstanding records';

    console.log('varName : ', varName);

    return varName;
  };

  const getPaymentErpNudgeButtonWarningText = (val: string) => {
    const varName = getWarningVarName(val);

    console.log('varName : ', varName);

    return `${varName} unavailable as it’s not integrated. Integrate your ERP to view it.`;
  };

  const isTextMessage = Object.keys(template)?.[0] === 'text';
  const templateName = Object.keys(template)?.[0] as TemplateKeys;
  const bannerData = getCampaignStatus(templateName, statusMap);

  const waConfigName = startCase(
    camelCase(waConfig?.displayName || waConfig?.whatsappPhoneNo || '')
  );

  const generateFileUrlPayload = (
    mimeType: 'image' | 'document'
  ): GenerateLinkParams => {
    if (msgType === MessageType.MEDIA && media?.[0]?.provider === 'redington') {
      return {
        id: media?.[0]?._id,
        waConfigId: media?.[0]?.waConfigId || waConfig?.id,
      } as RedingtonMediaRequest;
    }
    const payload = generateMediaParams(
      msgType,
      data,
      mimeType,
      media[0],
      msgId
    );
    return payload;
  };

  const locationData = message?.location
    ? {
        thumbnail: message.location.thumbnail,
        latitude: message.location.latitude,
        longitude: message.location.longitude,
      }
    : undefined;

  // Handle both single contact and array of contacts
  // Support two structures:
  // 1. message.contact.contacts (array) - WhatsApp format
  // 2. message.contact (single object or array) - legacy format
  const contactData = message?.contact
    ? (() => {
        const contactObj = message.contact as any;
        // Check if contacts array exists (WhatsApp format: message.contact.contacts)
        if (contactObj.contacts && Array.isArray(contactObj.contacts)) {
          return contactObj.contacts.map((item: any) => ({
            displayName: item.displayName,
            vcard: item.vcard,
            phoneNumber: parsePhoneFromVCard(item.vcard || ''),
          }));
        }
        // Fallback to legacy format: message.contact (single or array)
        const contacts = Array.isArray(contactObj) ? contactObj : [contactObj];
        return contacts.map((item: any) => ({
          displayName: item.displayName,
          vcard: item.vcard,
          phoneNumber: parsePhoneFromVCard(item.vcard || ''),
        }));
      })()
    : undefined;

  const s3MediaBucket = getConfigValue('S3_MEDIA_BUCKET')?.replace(/\/+$/, '');

  const knowledgeFiles = uniqBy(
    agent_metadata?.knowledge_files_referenced ?? [],
    'document_id'
  ).map(({ document_id, document_title, file_path }) => {
    const encodedFilePath = file_path
      .split('/')
      .filter(Boolean)
      .map((segment) => encodeURIComponent(segment))
      .join('/');

    return {
      id: document_id,
      title: document_title,
      url: `${s3MediaBucket}/${encodedFilePath}`,
    };
  });

  const messageTags = getMessageTags(message?.tags, actions?.tags);

  const stableMsgActions = useMemo(
    () => ({ actionsSection1, actionsSection2 }),
    [actionsSection1, actionsSection2]
  );

  const hasMessageTags = useMemo(
    () =>
      messageTag?.tag && messageTag?.sub_tag !== 'null' && messageTag?.sub_tag,
    [messageTag]
  );

  return {
    deleted,
    variant,
    userId,
    userName,
    avatarVariant: isBot ? 'bot' : isWhatsapp ? 'whatsapp' : 'user',
    userNameColor,
    delivered,
    seen,
    failedToSend,
    msgTime,
    buttons,
    systemButtons,
    shortText,
    template,
    isForwarded,
    isIssue,
    msgActions: stableMsgActions,
    actionsLoading,
    connectedThread,
    msgId,
    repliedForMessage,
    repliedForMessageData,
    label,
    isTextMessage,
    msgType: type,
    text,
    timetoken: String(timetoken),
    bannerData,
    messageTag,
    tagValue,
    subTagValue,
    waConfigName,
    waConfig,
    reactions: reactionList,
    channelId: channel,
    generateFileUrlPayload,
    customerId,
    intent,
    reply,
    getPaymentErpNudgeButtonWarningText,
    getWarningVarName,
    isInternalNote,
    locationData,
    contactData,
    cards,
    knowledgeFiles,
    messageTags,
    hasMessageTags,
    isNotSystemMessage,
  };
};
